import { useEffect, useRef, useState } from "react";
import { Button, Input, Modal } from "@legal-500/legal500-design-system";
import { AI_PARSE_MANUAL_UPLOAD_MAX_BYTES, DOCX_MIMETYPE, aiParser } from "@/api/aiParser.service";
import styles from "./AiParseRuns.module.css";

function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds - minutes * 60}s`;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Called once the upload is accepted, so the page can pick up the runs it starts. */
  onUploaded: (fileName: string) => void;
};

/**
 * Upload one .docx and put it through the real pipeline.
 *
 * This is the opposite trade from the Test action on the AI Schemas page. That one runs a single
 * schema synchronously, shows you the extraction and saves nothing. This one runs *everything* —
 * every enabled schema, their markdown templates, output written to blob storage, a parse run row
 * per schema — and shows you no content at all, because the result lives where a real submission's
 * result lives. What it deliberately does not do is invent a firm or a submission to hang the
 * document off: the upload stands alone, and the copy says so in as many words, because a run row
 * that looks like a firm's is the one mistake this feature can make.
 */
export function ManualUploadModal({ isOpen, onClose, onUploaded }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [publicationName, setPublicationName] = useState("");
  const [edition, setEdition] = useState("");
  const [submissionType, setSubmissionType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Opening the dialog is starting over. Anything left from the previous upload — a file, an
  // error, a publication name — would otherwise be submitted by someone who never typed it.
  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setFileError(null);
    setPublicationName("");
    setEdition("");
    setSubmissionType("");
    setUploading(false);
    setElapsedSeconds(0);
    setError(null);
    setIsDragging(false);
  }, [isOpen]);

  // A 10MB document over a slow connection is a long silence, and a button that says "Uploading…"
  // with no number on it is indistinguishable from a hung page.
  useEffect(() => {
    if (!uploading) return;
    const startedAt = Date.now();
    setElapsedSeconds(0);
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [uploading]);

  function chooseFile(chosen: File | undefined) {
    if (!chosen) return;
    if (!chosen.name.toLowerCase().endsWith(".docx")) {
      setFile(null);
      setFileError(`${chosen.name} is not a .docx — only Word documents can be parsed.`);
      return;
    }
    if (chosen.size > AI_PARSE_MANUAL_UPLOAD_MAX_BYTES) {
      setFile(null);
      setFileError(
        `${chosen.name} is ${formatMegabytes(chosen.size)} — the limit is ${formatMegabytes(
          AI_PARSE_MANUAL_UPLOAD_MAX_BYTES,
        )}.`,
      );
      return;
    }
    setFileError(null);
    setFile(chosen);
    setError(null);
  }

  async function upload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await aiParser.uploadManualDocument(file, { publicationName, edition, submissionType });
      onUploaded(file.name);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Upload a document to parse" size="mediumLarge">
      <div className={styles.modalBody}>
        <p className={styles.confirmText}>
          Runs one .docx through the whole parser exactly as a real submission goes through it:{" "}
          <strong>every enabled schema</strong>, their markdown templates, and the output written to
          blob storage. Each schema gets its own parse run in the table below, with the same live
          progress — and the same cost.
        </p>
        <p className={styles.warnNote}>
          The document is <strong>not attached to any firm</strong> and{" "}
          <strong>no submission is created</strong> for it. Nothing you upload here appears on a
          firm's record or in the submissions list; the only trace is the parse runs and the files
          they write.
        </p>

        <div
          className={`${styles.drop} ${isDragging ? styles.dropActive : ""}`}
          onDragOver={event => {
            event.preventDefault();
            if (!uploading) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={event => {
            event.preventDefault();
            setIsDragging(false);
            if (uploading) return;
            chooseFile(event.dataTransfer.files[0]);
          }}
        >
          <p className={styles.dropTitle}>
            {file ? file.name : "Drop a .docx here, or choose one"}
          </p>
          <p className={styles.dropHint}>
            A submission document, up to {formatMegabytes(AI_PARSE_MANUAL_UPLOAD_MAX_BYTES)}. It is
            stored in blob storage alongside the output it produces.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            aria-label="Submission .docx to parse"
            accept={`.docx,${DOCX_MIMETYPE}`}
            className={styles.hiddenInput}
            disabled={uploading}
            onChange={event => {
              const chosen = event.target.files?.[0];
              // Cleared so choosing the SAME file again still fires a change event — which is
              // exactly what re-uploading after a schema change looks like.
              event.target.value = "";
              chooseFile(chosen);
            }}
          />
          <div className={styles.dropActions}>
            <Button
              size="small"
              variant="outlined"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </Button>
          </div>
        </div>

        {fileError && (
          <div className={styles.errorBanner} role="alert">
            {fileError}
          </div>
        )}

        <div className={styles.modalFields}>
          <p className={styles.confirmNote}>
            Optional labels, recorded on the runs so this upload can be found in the filters later.
            They name nothing — no publication or edition is looked up from them.
          </p>
          <Input
            label="Publication"
            fullWidth
            value={publicationName}
            disabled={uploading}
            placeholder="e.g. Legal 500 EMEA"
            onChange={event => setPublicationName(event.target.value)}
          />
          <Input
            label="Edition"
            fullWidth
            value={edition}
            disabled={uploading}
            placeholder="e.g. 2027"
            onChange={event => setEdition(event.target.value)}
          />
          <Input
            label="Submission type"
            fullWidth
            value={submissionType}
            disabled={uploading}
            placeholder="e.g. solicitor"
            onChange={event => setSubmissionType(event.target.value)}
          />
        </div>

        {uploading && (
          <div className={styles.runningRow} role="status" aria-live="polite">
            <span className={styles.spinner} aria-hidden="true" />
            <span>
              Uploading… {formatElapsed(elapsedSeconds)} elapsed. Parsing starts once the file is
              stored, and the runs appear in the table on their own.
            </span>
          </div>
        )}

        {error && !uploading && (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        )}

        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!file || uploading} onClick={() => void upload()}>
            {uploading ? "Uploading…" : "Upload and parse"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
